/**
 * Global streaming registry for direct communication with StreamingAgentMessage components.
 * This allows bypassing context updates during streaming for optimal performance.
 *
 * WHY THIS UNCONVENTIONAL APPROACH?
 *
 * React's default rendering model causes performance issues with high-frequency streaming:
 * - Traditional Context updates trigger cascade re-renders throughout the component tree
 * - Each streaming token could cause hundreds of components to re-render unnecessarily
 * - React's batching doesn't help with external streaming events arriving rapidly
 * - useMemo/useCallback/React.memo couldn't prevent the cascading updates from contexts
 *
 * This registry pattern solves the problem by:
 * 1. Direct component-to-component communication (no React reconciliation)
 * 2. Only the target streaming component re-renders when receiving updates
 * 3. Context state remains stable during streaming (no cascade triggers)
 * 4. Maintains React's unidirectional data flow at the application level
 *
 * FUTURE CONSIDERATIONS:
 *
 * This approach could be replaced when:
 * - React 19+ brings better streaming/concurrent features to Backstage
 * - React Compiler becomes available and can optimize away unnecessary re-renders
 * - Backstage upgrades React enough to leverage useEffectEvent or similar APIs
 * - We opt for a more sophisticated state management library that handles high-frequency updates, zustand for example.
 *
 * The registry is isolated and can be easily removed once better React patterns
 * become available without affecting the rest of the application architecture.
 */

import type { AIActivity, EnhancedFileCitation } from '../types/chat.types';

/**
 * Controller interface for streaming message components.
 * Provides methods for receiving real-time streaming updates.
 */
export interface StreamingMessageController {
  /**
   * Updates the content of the streaming message with new tokens.
   */
  updateContent: (content: string) => void;

  /**
   * Updates the current activity state (thinking, searching, analyzing, outputting, completed).
   */
  updateActivity: (activity: AIActivity) => void;

  /**
   * Updates the citations array with new citations discovered during streaming.
   */
  updateCitations: (citations: EnhancedFileCitation[]) => void;

  /**
   * Updates the reasoning summary text being streamed before the final output.
   */
  updateReasoningSummary: (text: string) => void;

  /**
   * Completes the streaming with final content and citations.
   */
  complete: (finalContent: string, citations: EnhancedFileCitation[]) => void;

  /**
   * Reports an error that occurred during streaming.
   */
  error: (error: Error) => void;
}

class StreamingRegistry {
  private controllers: Map<string, StreamingMessageController> = new Map();
  private lastActivity: Map<string, AIActivity> = new Map();
  private lastContent: Map<string, string> = new Map();

  /**
   * Registers a streaming message component for direct updates.
   */
  register(messageId: string, controller: StreamingMessageController): void {
    this.controllers.set(messageId, controller);
    // Initialize tracking for deduplication
    this.lastActivity.delete(messageId);
    this.lastContent.delete(messageId);
  }

  /**
   * Unregisters a streaming message component.
   */
  unregister(messageId: string): void {
    this.controllers.delete(messageId);
    this.lastActivity.delete(messageId);
    this.lastContent.delete(messageId);
  }

  /**
   * Checks if a streaming message component is registered for the given ID.
   */
  hasController(messageId: string): boolean {
    return this.controllers.has(messageId);
  }

  /**
   * Updates content for a streaming message component.
   * Deduplicates identical consecutive content updates.
   */
  updateContent(messageId: string, content: string): boolean {
    const controller = this.controllers.get(messageId);
    if (!controller) {
      return false;
    }

    // Avoid redundant updates with identical content
    const lastContent = this.lastContent.get(messageId);
    if (lastContent === content) {
      return true; // Already up to date
    }

    this.lastContent.set(messageId, content);
    controller.updateContent(content);
    return true;
  }

  /**
   * Updates activity for a streaming message component.
   * Deduplicates identical consecutive activity updates.
   */
  updateActivity(messageId: string, activity: AIActivity): boolean {
    const controller = this.controllers.get(messageId);
    if (!controller) {
      return false;
    }

    // Avoid redundant updates with same activity
    const lastActivity = this.lastActivity.get(messageId);
    if (lastActivity === activity) {
      return true;
    }
    this.lastActivity.set(messageId, activity);
    controller.updateActivity(activity);
    return true;
  }

  /**
   * Updates citations for a streaming message component.
   */
  updateCitations(
    messageId: string,
    citations: EnhancedFileCitation[],
  ): boolean {
    const controller = this.controllers.get(messageId);
    if (controller) {
      controller.updateCitations(citations);
      return true;
    }
    return false;
  }

  /**
   * Updates reasoning summary for a streaming message component.
   */
  updateReasoningSummary(messageId: string, text: string): boolean {
    const controller = this.controllers.get(messageId);
    if (controller) {
      controller.updateReasoningSummary(text);
      return true;
    }
    return false;
  }

  /**
   * Completes streaming for a message component.
   */
  complete(
    messageId: string,
    finalContent: string,
    citations: EnhancedFileCitation[],
  ): boolean {
    const controller = this.controllers.get(messageId);
    if (!controller) {
      return false;
    }

    controller.complete(finalContent, citations);
    // Clean up tracking data as streaming is complete
    this.lastActivity.delete(messageId);
    this.lastContent.delete(messageId);
    return true;
  }

  /**
   * Reports an error to a streaming message component.
   */
  error(messageId: string, error: Error): boolean {
    const controller = this.controllers.get(messageId);
    if (!controller) {
      return false;
    }

    controller.error(error);
    // Clean up tracking data on error
    this.lastActivity.delete(messageId);
    this.lastContent.delete(messageId);
    return true;
  }
}

// Global registry instance
export const streamingRegistry = new StreamingRegistry();
